import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface PatientMessage {
  id: string;
  clinic_id: string;
  patient_id: string;
  sender: 'patient' | 'clinic';
  sender_user_id: string | null;
  sender_name: string | null;
  content: string;
  read_at: string | null;
  read_by: string | null;
  created_at: string;
  updated_at: string;
}

export const patientMessagesQueryKey = (patientId: string | null) =>
  ['messages', 'patient', patientId] as const;

export function usePatientMessages(patientId: string | null | undefined) {
  return useQuery({
    queryKey: patientMessagesQueryKey(patientId ?? null),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<PatientMessage[]> => {
      if (!patientId) return [];
      
      const { data, error } = await supabase.rpc('get_patient_messages', {
        p_patient_id: patientId,
      });

      if (error) throw error;
      
      return (data ?? []) as PatientMessage[];
    },
    staleTime: 15_000,
  });
}

export function useSendPatientMessage() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: { patient_id: string; content: string }) => {
      const { data, error } = await supabase.rpc('send_patient_message', {
        p_patient_id: params.patient_id,
        p_content: params.content,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate the messages query for this patient
      queryClient.invalidateQueries({
        queryKey: patientMessagesQueryKey(variables.patient_id),
      });
    },
  });
}

export function useMarkMessageRead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: { message_id: string; patient_id: string }) => {
      const { data, error } = await supabase.rpc('mark_message_read', {
        p_message_id: params.message_id,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate the messages query for this patient
      queryClient.invalidateQueries({
        queryKey: patientMessagesQueryKey(variables.patient_id),
      });
    },
  });
}
